import React, {forwardRef, useRef, useState} from "react";
import PropTypes from "prop-types";
import {ATooltip} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {useTranslation} from "react-i18next";
import css from "./DrawerTitle.module.scss";

const DrawerTitle = forwardRef(
  ({title, ariaLabel, className: propsClassName, ...rest}, propsRef) => {
    const {t} = useTranslation();
    const titleRef = useRef(null);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const titleString = ariaLabel
      ? ariaLabel
      : title || t("drawers.labels.common.emptyTitle");

    return (
      <>
        <h3
          aria-label={titleString}
          className={classNames(css.title, propsClassName)}
          onMouseEnter={() => setIsTooltipOpen(true)}
          onMouseLeave={() => setIsTooltipOpen(false)}
          // Use `ref` callback function to handle our ref
          // and a possible forwardRef
          ref={(node) => {
            if (propsRef) {
              typeof propsRef === "function"
                ? propsRef(node)
                : (propsRef.current = node);
            }
            titleRef.current = node;
          }}
          {...rest}>
          {title || t("drawers.labels.common.emptyTitle")}
        </h3>
        <ATooltip
          pointer
          anchorRef={titleRef}
          className={css.tooltip}
          open={isTooltipOpen}
          placement="bottom-left">
          {titleString}
        </ATooltip>
      </>
    );
  }
);

export default DrawerTitle;

DrawerTitle.propTypes = {
  /**
   * Title to show at the top of the drawer
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * If a component is passed for `title`, you can use this to pass in a string
   * to be used for the tooltip and aria-label attribute
   *
   * @remark Useful for titles that need show an entity with a pivot menu
   */
  ariaLabel: function (props, propName, componentName) {
    if (props[propName] && typeof props[propName] !== "string") {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof props[
          propName
        ]}\` supplied to ${componentName}, expected \`string\``
      );
    }

    if (!props[propName] && typeof props.title === "object") {
      return new Error(
        `\`${propName}\` is required in ${componentName} when \`title\` is not a string`
      );
    }
  }
};
